import { forwardRef } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'

import { cn } from '@/lib/utils'
import { FxIcon } from './FxIcon'

/**
 * One field of a template: listed as a "Komponente" in the editor's sidebar
 * and as a read value in the review. The field type has a fixed glyph and is
 * never chosen freely.
 *
 * The four states are two pairs. `idle`/`selected` are the editor — the chip
 * sits on `fill`, and the selected one turns `primary-subtle` with its name
 * highlighted for renaming and a trash button. `result`/`error` are the
 * review — the chip sits on `ground` (or `danger-subtle`) and carries the read
 * value under the name; an unreadable field reads "undefiniert".
 *
 * The chip is 248px in the sidebar, which every screen that lays it out in a
 * grid overrides with `className="w-auto"`.
 */
const FIELD_ICON = {
  text: 'stylus_note',
  checkbox: 'disabled_by_default',
  signature: 'signature',
} as const

const chipStyles = cva(
  'flex min-h-control w-[248px] max-w-full items-center gap-4 rounded-sm py-2 pl-[14px] pr-3 text-text',
  {
    variants: {
      state: {
        idle: 'bg-fill',
        selected: 'bg-primary-subtle',
        result: 'bg-ground',
        error: 'bg-danger-subtle',
      },
      // A field that belongs to the template but is not placed on the page
      // being edited: white with a hairline instead of the solid fill, so it
      // reads as available rather than as present.
      muted: { true: 'bg-surface shadow-[inset_0_0_0_1.5px_hsl(var(--fx-fill))]', false: '' },
      active: { true: 'outline outline-2 -outline-offset-2', false: '' },
    },
    compoundVariants: [
      { active: true, state: 'error', class: 'outline-danger' },
      { active: true, state: 'idle', class: 'outline-primary' },
      { active: true, state: 'selected', class: 'outline-primary' },
      { active: true, state: 'result', class: 'outline-primary' },
    ],
    defaultVariants: { state: 'idle', muted: false, active: false },
  },
)

export interface FxFieldChipProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    VariantProps<typeof chipStyles> {
  type?: keyof typeof FIELD_ICON
  name: string
  /** The read value, shown under the name in `result` and `error`. */
  value?: string
  /**
   * Beyond the design system's contract: a second line under the name for a
   * remark rather than a value ("nur in 2018" in the layout editor).
   */
  note?: string
  /**
   * Beyond the contract: something at the right end — the confidence figure in
   * the review, the "Platzieren" button in the editor. The delete button takes
   * this place in the `selected` state.
   */
  trailing?: React.ReactNode
  onDelete?: () => void
}

export const FxFieldChip = forwardRef<HTMLDivElement, FxFieldChipProps>(
  (
    { className, type = 'text', name, state = 'idle', muted, active, value, note, trailing, onDelete, ...props },
    ref,
  ) => {
    const isReview = state === 'result' || state === 'error'
    const shownValue = state === 'error' ? (value ?? 'undefiniert') : value
    const secondLine = shownValue ?? note

    return (
      <div ref={ref} className={cn(chipStyles({ state, muted, active }), className)} {...props}>
        <FxIcon
          name={FIELD_ICON[type]}
          size={22}
          className={cn(
            'shrink-0',
            state === 'error' ? 'text-danger' : muted ? 'text-text-muted' : 'text-primary',
          )}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          <span
            className={cn(
              'truncate',
              isReview ? 'text-body-strong' : 'text-body',
              // Renaming happens in place, so the name is drawn as the field
              // it is about to become. The negative margin keeps the
              // highlight's padding from moving the text.
              state === 'selected' && 'ml-[-4px] max-w-full self-start bg-primary-tonal px-1',
            )}
          >
            {name}
          </span>
          {secondLine && (
            <span
              className={cn(
                'truncate',
                shownValue
                  ? cn('text-body', state === 'error' && 'text-danger-text')
                  : 'text-caption text-text-muted',
              )}
            >
              {secondLine}
            </span>
          )}
        </div>
        {state === 'selected' && onDelete ? (
          <button
            type="button"
            onClick={onDelete}
            aria-label={`${name} entfernen`}
            className="flex shrink-0 text-text-muted"
          >
            <FxIcon name="delete" size={22} />
          </button>
        ) : (
          trailing && <div className="shrink-0">{trailing}</div>
        )}
      </div>
    )
  },
)
FxFieldChip.displayName = 'FxFieldChip'
